"use client"

import { useState, useCallback } from "react"
import { useAuth } from "../context/AuthContext"
import type { SyncOption, ApiSyncOption, SyncOptionsResponse } from "../types/choose-sync"
import { DEFAULT_SYNC_OPTIONS, CHOOSE_SYNC_CONSTANTS } from "../constants/choose-sync"
import { generateIconForObject } from "../utils/icon-generator"

export const useSyncOptions = () => {
  const [syncOptions, setSyncOptions] = useState<SyncOption[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const { getConnectors } = useAuth()

  const transformApiOptions = useCallback((apiOptions: ApiSyncOption[]): SyncOption[] => {
    return apiOptions.map((item) => ({
      id: item.id,
      name: `${item.source_system} ${item.source_object} → ${item.target_system} ${item.target_object}`,
      description: `Sync ${item.source_object} from ${item.source_system} to ${item.target_object} in ${item.target_system}`,
      source_system: item.source_system,
      source_object: item.source_object,
      target_system: item.target_system,
      target_object: item.target_object,
      supported_directions: item.supported_directions || "one-way",
      icon: generateIconForObject(item.source_object),
    }))
  }, [])

  const getDefaultOptionsWithIcons = useCallback((): SyncOption[] => {
    return DEFAULT_SYNC_OPTIONS.map((option) => ({
      ...option,
      icon: generateIconForObject(option.source_object),
    }))
  }, [])

  const fetchSyncOptions = useCallback(async () => {
    try {
      setLoading(true)
      setError(null)

      const response: SyncOptionsResponse = await getConnectors()

      if (response.status === 200 && response.data) {
        const transformedOptions = transformApiOptions(response.data)
        setSyncOptions(transformedOptions)
      } else {
        // Fallback to default options
        const defaultOptions = getDefaultOptionsWithIcons()
        setSyncOptions(defaultOptions)
      }
    } catch (err: any) {
      console.error("Error fetching sync options:", err)
      const errorMessage = err.userMessage || err.message || CHOOSE_SYNC_CONSTANTS.ERROR_MESSAGES.FETCH_FAILED
      setError(errorMessage)

      // Set default options even on error
      const defaultOptions = getDefaultOptionsWithIcons()
      setSyncOptions(defaultOptions)
    } finally {
      setLoading(false)
    }
  }, [getConnectors, transformApiOptions, getDefaultOptionsWithIcons])

  const retryFetch = useCallback(() => {
    fetchSyncOptions()
  }, [fetchSyncOptions])

  return {
    syncOptions,
    loading,
    error,
    fetchSyncOptions,
    retryFetch,
  }
}
